/**
 * HTTP error helpers — narrow `unknown` errors raised by axios / TanStack
 * Query into typed status / payload checks without sprinkling `as any` at
 * every call-site. Works for both `useQuery({ error })` payloads and
 * `try/catch` paths where axios attaches the response on the rejection.
 *
 * Structural over nominal: we avoid `axios.isAxiosError` so the same
 * helpers work for errors that get wrapped or re-thrown by TanStack Query
 * / mutation layers, as long as the `response.status` / `response.data`
 * shape is preserved.
 */

export function getHttpStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  if (!('response' in error)) return undefined;
  const response = (error as { response?: unknown }).response;
  if (typeof response !== 'object' || response === null) return undefined;
  const status = (response as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}

export function getResponseData<T = unknown>(error: unknown): T | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  if (!('response' in error)) return undefined;
  const response = (error as { response?: unknown }).response;
  if (typeof response !== 'object' || response === null) return undefined;
  const data = (response as { data?: unknown }).data;
  return data as T | undefined;
}

export function isHttpStatus(error: unknown, status: number): boolean {
  return getHttpStatus(error) === status;
}

export function is401(error: unknown): boolean {
  return isHttpStatus(error, 401);
}

export function is409(error: unknown): boolean {
  return isHttpStatus(error, 409);
}

export function is422(error: unknown): boolean {
  return isHttpStatus(error, 422);
}

export function is429(error: unknown): boolean {
  return isHttpStatus(error, 429);
}

export function is5xx(error: unknown): boolean {
  const status = getHttpStatus(error);
  return status !== undefined && status >= 500 && status < 600;
}

export function getErrorCode(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

/**
 * Backend *business* error code, read from `response.data.error.code`
 * (e.g. `KYC-001`, `NO_TANKEF_ACCOUNT`, `STEP_UP_REQUIRED`).
 *
 * Distinct from {@link getErrorCode}, which reads the axios *transport*
 * code (`error.code`, e.g. `ERR_NETWORK`/`ECONNABORTED`) and is therefore
 * blind to the backend's business codes. Use this helper for code-based
 * routing decisions; use `getErrorCode` only when you care about the
 * transport-level failure.
 */
export function getBackendErrorCode(error: unknown): string | undefined {
  const data = getResponseData<{ error?: { code?: unknown } }>(error);
  const code = data?.error?.code;
  return typeof code === 'string' ? code : undefined;
}

export function getErrorMessage(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const message = (error as { message?: unknown }).message;
  return typeof message === 'string' ? message : undefined;
}

export function extractBackendErrorMessage(error: unknown, fallback: string): string {
  const data = getResponseData<{ error?: { message?: unknown; details?: unknown } }>(error);
  const backendError = data?.error;
  if (backendError && typeof backendError.message === 'string') {
    const details = Array.isArray(backendError.details)
      ? backendError.details.filter((d): d is string => typeof d === 'string')
      : [];
    return details.length > 0
      ? `${backendError.message}: ${details.join(', ')}`
      : backendError.message;
  }
  return fallback;
}
